import {
  Capability,
  FeatureWithoutPermissionsMapping,
  UnimplementedCapability,
  USER_PREFERENCE_GATED_CAPABILITIES,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';

import {StandardFeature} from './CapabilityConstants';
import {getCapabilityFeatureFromStandardFeature, legacyIsCapabilityEnabled} from './getCapabilityFeatures';

// Applications that do not have types in search/listings
export const CAPABILITIES_WITHOUT_LISTING_TYPE = [Capability.RESULTS] as const;
type CapabilityWithoutListingType = (typeof CAPABILITIES_WITHOUT_LISTING_TYPE)[number];
export type CapabilityWithListingType = Exclude<
  Capability,
  CapabilityWithoutListingType | UnimplementedCapability
>;

let tenantProvisionableCapabilities: Set<Capability> | null =
  makeTenantProvisionableCapabilitiesSetFromBootstrap();

function makeTenantProvisionableCapabilitiesSetFromBootstrap(): Set<Capability> | null {
  if (!window.bootstrap?.tenantProvisionableCapabilities) {
    return null;
  }
  return new Set(window.bootstrap.tenantProvisionableCapabilities);
}

// Only for manipulating tenantProvisionableCapabilities in tests
export function _setEnabledTenantProvisionableCapabilities(capabilities: Set<Capability>): void {
  tenantProvisionableCapabilities = capabilities;
}

function isCapabilityEnabledForListingThroughCapabilityManagement(
  capability: CapabilityWithListingType
): boolean {
  const userPrefGatedCapabiltities =
    USER_PREFERENCE_GATED_CAPABILITIES as unknown as Array<CapabilityWithListingType>;
  // If this capability is not gated with isFeatureEnabled, we fall back to the legacy config flag value
  if (userPrefGatedCapabiltities.includes(capability)) {
    return legacyIsCapabilityEnabled(capability);
  }
  // If window.bootstrap.tenantProvisionableCapabilities does not exist, this code is being loaded outside of
  // the main "editor" template. But, all listing code is only accessible through the main editor template so
  // we can just return true here.
  if (!tenantProvisionableCapabilities) {
    return true;
  }

  const listingFeature = getCapabilityFeatureFromStandardFeature(capability, StandardFeature.OPEN);
  return (
    tenantProvisionableCapabilities.has(capability) ||
    (Boolean(listingFeature) &&
      isFeatureEnabled({feature: listingFeature as FeatureWithoutPermissionsMapping}))
  );
}

// We treat search / listings as a special case because we do not hide types / results for capabilities you
// do not have access to. But, we do limit the types / results to capabilities that are "provisionable" on the
// tenant OR that you were explicitly given access to.
export function isCapabilityEnabledForListing(capability: CapabilityWithListingType): boolean {
  if (BenchlingConfig.IDENTITY_ENABLE_CAPABILITY_GATING) {
    return isCapabilityEnabledForListingThroughCapabilityManagement(capability);
  }
  // If Capability Management is turned off, use the legacy ENABLE_[APPLICATION] flag
  return legacyIsCapabilityEnabled(capability);
}
